import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Turn FastAPI 422 detail arrays into plain text so toasts never crash the page
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;
    if (data && Array.isArray(data.detail)) {
      data.detail = data.detail.map((d) => (d && d.msg) || JSON.stringify(d)).join(', ');
    } else if (data && data.detail && typeof data.detail === 'object') {
      data.detail = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
    }
    return Promise.reject(error);
  }
);


const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('bitz_token'));
  const [loading, setLoading] = useState(true);

  // ============================================================================
  // Global axios auth interceptor — runs ONCE per mount.
  //
  // Why this exists:
  //   Many older admin/member pages read `localStorage.getItem('token')` directly
  //   (wrong key — real key is `bitz_token`). Without an interceptor those pages
  //   send `Authorization: Bearer null` and 401. Rather than touching 40+ pages,
  //   this interceptor unconditionally injects the correct auth header on every
  //   request, healing the inconsistency platform-wide and preventing the bug
  //   from re-appearing as new pages are added.
  //
  //   It also auto-logs out on 401 so stale tokens flush cleanly.
  // ============================================================================
  useEffect(() => {
    const reqId = axios.interceptors.request.use(
      (config) => {
        const t = localStorage.getItem('bitz_token');
        if (t) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${t}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    const resId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only force-logout on /api/* 401s — third-party 401s (e.g. external news APIs)
        // should not boot the user.
        const url = error?.config?.url || '';
        if (error?.response?.status === 401 && url.includes('/api/')) {
          localStorage.removeItem('bitz_token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Clear invalid token and don't show loading forever
      localStorage.removeItem('bitz_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password) => {
    const response = await axios.post(`${API}/auth/login`, { identifier, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('bitz_token', access_token);
    localStorage.removeItem('bitz_must_change_pwd');
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('bitz_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('bitz_token');
    localStorage.removeItem('bitz_must_change_pwd');
    setToken(null);
    setUser(null);
  };

  // Manual login with token and user data (for registration flow)
  const setAuthData = (accessToken, userData) => {
    localStorage.setItem('bitz_token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const isAdmin = () => user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = () => user?.role === 'super_admin';
  const isTelecaller = () => user?.role === 'telecaller';
  const isMember = () => user?.role === 'member';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      setAuthData,
      isAdmin,
      isTelecaller,
      isMember,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Axios interceptor for auth
export const setupAxiosInterceptors = (token, logout) => {
  axios.interceptors.request.use(
    (config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );
};
